import React from 'react'
import { Box, Flex, Icon, Spinner, Text } from '@chakra-ui/react'
import { FaWandMagicSparkles } from 'react-icons/fa6'
import MarkdownMessage from './markdown-message'
import ProposeChangeCard from './propose-change-card'
import { splitMessageContent } from './propose-change'

/**
 * Renders an assistant message, turning any `%= propose_change =%` blocks
 * into interactive approval cards while rendering the surrounding prose as
 * normal markdown.
 *
 * While `isStreaming`, an incomplete trailing proposal block is replaced with
 * a "Creating proposal…" placeholder so nobody watches raw delimiter/JSON
 * type out. Settled messages keep malformed or truncated blocks as plain
 * text, which is what an operator needs in order to debug them.
 */
interface AssistantMessageContentProps {
    content: string
    /** Scopes Applied-state persistence for proposal cards in this message. */
    conversationId: string | null
    /** Forwarded to each proposal card — fires after a successful Apply. */
    onProposalApplied?: () => void
    /** Live bubble: hide incomplete proposal markup behind a placeholder. */
    isStreaming?: boolean
}

export default function AssistantMessageContent({
    content,
    conversationId,
    onProposalApplied,
    isStreaming = false,
}: AssistantMessageContentProps) {
    const segments = splitMessageContent(content, {
        hideIncompleteBlocks: isStreaming,
    })

    // Fast path: no proposal blocks → render exactly as before.
    if (segments.length === 1 && segments[0].type === 'text') {
        return <MarkdownMessage content={segments[0].value} />
    }

    return (
        <>
            {segments.map((segment, i) => {
                if (segment.type === 'proposal') {
                    return (
                        <ProposeChangeCard
                            key={`proposal-${i}`}
                            proposal={segment.proposal}
                            conversationId={conversationId}
                            onApplied={onProposalApplied}
                        />
                    )
                }
                if (segment.type === 'proposal_pending') {
                    return <CreatingProposalPlaceholder key={`pending-${i}`} />
                }
                // Skip whitespace-only text between blocks so we don't render
                // empty markdown paragraphs around the cards.
                if (segment.value.trim().length === 0) return null
                return (
                    <MarkdownMessage
                        key={`text-${i}`}
                        content={segment.value}
                    />
                )
            })}
        </>
    )
}

function CreatingProposalPlaceholder() {
    return (
        <Box
            borderWidth={1}
            borderColor="brand.200"
            borderRadius="md"
            bg="white"
            my={3}
            overflow="hidden"
        >
            <Flex bg="brand.50" px={4} py={3} gap={2} align="center">
                <Icon
                    as={FaWandMagicSparkles}
                    boxSize={3.5}
                    color="brand.600"
                    flexShrink={0}
                />
                <Spinner size="xs" color="brand.500" />
                <Text fontSize="sm" fontWeight="medium" color="brand.700">
                    Creating proposal…
                </Text>
            </Flex>
        </Box>
    )
}
